import { useNavigate } from 'react-router-dom';
import { OnboardingLayout, PersonalInfoForm } from '@/components/onboarding';
import { useOnboarding } from '@/hooks/use-onboarding';
import { useAuthStore } from '@/store/auth-store';
import { useOnboardingStore } from '@/store/onboarding-store';
import { BUSINESS_CONFIG, ROUTES } from '@/lib/constants';
import type { PersonalInfoData } from '@/types/profile';

export function PersonalInfo() {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const isSupervisor = user?.userRole === 'SUPERVISOR';

  const { isLoading, savePersonalInfo, goToNextStep } = useOnboarding({
    totalSteps: BUSINESS_CONFIG.ONBOARDING.TOTAL_STEPS,
    steps: BUSINESS_CONFIG.ONBOARDING.STEPS,
  });

  // Read initial data directly from persisted store
  const { data, pendingProfilePhotoFile, setPendingProfilePhotoFile } = useOnboardingStore();

  const handleNext = async (data: PersonalInfoData) => {
    const success = await savePersonalInfo(data);

    if (success) {
      // Navigate to next step
      const navigated = goToNextStep();
      if (!navigated) {
        navigate(ROUTES.ONBOARDING.TERMS);
      }
    }
  };

  // Get initial data from persisted store
  const initialData = data.personalInfo;

  return (
    <OnboardingLayout
      currentStep={1}
      totalSteps={BUSINESS_CONFIG.ONBOARDING.TOTAL_STEPS}
      title={isSupervisor ? 'Supervisor Info' : 'Tell us about yourself'}
      subtitle={
        isSupervisor
          ? 'Add your name and phone number for your supervisor account.'
          : "We'll use this information to create your caregiver profile"
      }
    >
      <PersonalInfoForm
        onNext={handleNext}
        initialData={initialData}
        initialProfilePhotoFile={isSupervisor ? null : pendingProfilePhotoFile}
        onProfilePhotoChange={isSupervisor ? undefined : setPendingProfilePhotoFile}
        isLoading={isLoading}
        mode={isSupervisor ? 'supervisor' : 'caregiver'}
      />
    </OnboardingLayout>
  );
}
